import { useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type ColGroupDef,
  type ICellRendererParams,
} from 'ag-grid-community'
import {
  AlertTriangle,
  Check,
  Columns3,
  MessageCircle,
  Pencil,
  RotateCcw,
  Search,
  SlidersHorizontal,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import {
  formatPercent,
  formatUnits,
  formatWaveDate,
  validateWavePercent,
  weekLabel,
} from './model'
import {
  aggregateSkuRows,
  isBalanced,
  type SkuWaveAllocation,
  type SkuWaveColumn,
  type SkuWaveLine,
  type SkuWaveRow,
} from './sku-model'
import { SkuPercentEditor } from './SkuPercentEditor'
import './wave-planning.css'

ModuleRegistry.registerModules([AllCommunityModule])

const referenceColumns: {
  field: keyof SkuWaveLine
  label: string
  kind: 'text' | 'units' | 'money' | 'decimal'
  width?: number
}[] = [
  { field: 'vendor', label: 'Vendor', kind: 'text', width: 250 },
  { field: 'stores', label: 'Store Count', kind: 'units' },
  { field: 'cp', label: 'Case Pack (CP)', kind: 'units' },
  { field: 'ip', label: 'Inner Pack (IP)', kind: 'units' },
  { field: 'cube', label: 'Cube (ft³)', kind: 'decimal' },
  { field: 'cost', label: 'US Cost', kind: 'money' },
  { field: 'retail', label: 'US RTL', kind: 'money' },
  {
    field: 'childPurchaseUnits',
    label: 'Child Purchase Units',
    kind: 'units',
    width: 150,
  },
  {
    field: 'purchaseQty',
    label: 'PIAB + Single SKU Purchase Qty',
    kind: 'units',
    width: 180,
  },
  { field: 'moq', label: 'MOQ', kind: 'units' },
  { field: 'pogSet', label: 'POG Set', kind: 'units' },
  { field: 'pogMax', label: 'POG Max Qty', kind: 'units' },
  {
    field: 'totalInnerPacks',
    label: "TTL IP's",
    kind: 'units',
    width: 140,
  },
  {
    field: 'avgStoreUnits',
    label: 'AVG STR',
    kind: 'units',
    width: 140,
  },
]

const decimal = (value: number) =>
  value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
const money = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })

function ProductCell({
  data,
  skuOnly = false,
}: ICellRendererParams<SkuWaveRow> & { skuOnly?: boolean }) {
  if (!data) return null
  if (!data.line)
    return (
      <div className="leading-tight">
        <strong>Filtered totals</strong>
        {!skuOnly && (
          <p className="mt-1 text-[10px] font-normal text-muted-foreground">
            Units exclude Parent · Cube excludes Child
          </p>
        )}
      </div>
    )
  return (
    <div className="min-w-0 py-1 leading-tight">
      <div className="flex items-center gap-1.5 text-[11px]">
        <span className="font-medium">{data.line.sku}</span>
        {data.adjusted && (
          <span className="text-[10px] text-primary">Adjusted</span>
        )}
        {data.needsReview && (
          <AlertTriangle
            size={12}
            className="shrink-0 text-amber-700"
            aria-label="Needs review"
          />
        )}
      </div>
      {!skuOnly && (
        <p className="mt-1 truncate text-xs" title={data.line.description}>
          {data.line.description}
        </p>
      )}
      {!skuOnly && (
        <p className="mt-1 text-[10px] text-muted-foreground sm:hidden">
          {data.line.type} · {formatPercent(data.totalPercent ?? 0)} allocated
        </p>
      )}
    </div>
  )
}

function AllocationCell({ data }: ICellRendererParams<SkuWaveRow>) {
  if (!data || data.totalPercent === null) return '—'
  const balanced = isBalanced(data.totalPercent)
  return (
    <div
      className={`w-full text-right leading-tight ${data.needsReview ? 'text-amber-800' : ''}`}
    >
      <span className="font-medium">{formatPercent(data.totalPercent)}</span>
      {!balanced && (
        <p className="mt-1 text-[10px]">
          {formatPercent(Math.abs(1 - data.totalPercent))}{' '}
          {data.totalPercent < 1 ? 'remaining' : 'over'}
        </p>
      )}
      {data.outsideWeeks.length > 0 && (
        <p className="mt-1 text-[10px]">Outside plan</p>
      )}
    </div>
  )
}

interface Props {
  rows: SkuWaveRow[]
  weeks: SkuWaveColumn[]
  commentCounts: Record<string, number>
  onEdit: (lineId: string, index: number, percent: string) => void
  onResetRow: (lineId: string) => void
  onOpenComments: (line: SkuWaveLine, trigger: HTMLButtonElement) => void
}

export function SkuWaveGrid({
  rows,
  weeks,
  commentCounts,
  onEdit,
  onResetRow,
  onOpenComments,
}: Props) {
  const mobile = useMediaQuery('(max-width: 639px)')
  const grid = useRef<AgGridReact<SkuWaveRow>>(null)
  const [query, setQuery] = useState('')
  const [reviewOnly, setReviewOnly] = useState(false)
  const [references, setReferences] = useState<string[]>([])
  const [separateProduct, setSeparateProduct] = useState(false)
  const [metrics, setMetrics] = useState(false)
  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase()
    return rows.filter(
      row =>
        (!reviewOnly || row.needsReview) &&
        (!search ||
          `${row.line?.sku} ${row.line?.description} ${row.line?.vendor}`
            .toLowerCase()
            .includes(search))
    )
  }, [rows, query, reviewOnly])
  const totals = useMemo(
    () => [aggregateSkuRows(filtered, weeks)],
    [filtered, weeks]
  )

  const columns = useMemo<
    (ColDef<SkuWaveRow> | ColGroupDef<SkuWaveRow>)[]
  >(() => {
    const numeric = (
      field: keyof SkuWaveRow,
      label: string,
      width: number
    ): ColDef<SkuWaveRow> => ({
      colId: field,
      headerName: label,
      width,
      valueGetter: params => params.data?.[field],
      cellClass: 'ag-right-aligned-cell wave-number',
      headerClass: 'ag-right-aligned-header',
      valueFormatter: params =>
        params.value == null ? '—' : formatUnits(Number(params.value)),
    })
    return [
      {
        colId: 'product',
        headerName: 'Product',
        width: mobile ? 148 : 260,
        pinned: 'left',
        lockPinned: true,
        valueGetter: params =>
          params.data?.line
            ? `${params.data.line.sku} ${params.data.line.description}`
            : 'Filtered totals',
        cellRenderer: ProductCell,
        cellClass: 'sku-product-cell',
        hide: separateProduct,
      },
      {
        colId: 'sku',
        headerName: 'SKU',
        width: mobile ? 148 : 165,
        pinned: 'left',
        lockPinned: true,
        hide: !separateProduct,
        valueGetter: params => params.data?.line?.sku,
        cellRenderer: ProductCell,
        cellRendererParams: { skuOnly: true },
        cellClass: 'sku-product-cell',
      },
      {
        colId: 'description',
        headerName: 'Description',
        width: 270,
        hide: !separateProduct,
        valueGetter: params => params.data?.line?.description,
        tooltipValueGetter: params => params.data?.line?.description,
      },
      {
        colId: 'type',
        headerName: 'Type',
        headerTooltip: 'Parent / Child / Single SKU / Replen',
        width: 102,
        hide: mobile && !separateProduct,
        valueGetter: params => params.data?.line?.type,
        cellRenderer: ({ value }: ICellRendererParams<SkuWaveRow>) =>
          value && (
            <span
              className={`rounded px-1.5 py-1 text-[11px] ${value === 'Parent' ? 'bg-accent font-medium text-primary' : 'bg-secondary text-muted-foreground'}`}
            >
              {value}
            </span>
          ),
      },
      numeric('totalQty', 'TTL Qty', 108),
      {
        ...numeric('totalPercent', 'Total %', 118),
        cellRenderer: AllocationCell,
      },
      ...referenceColumns.map(
        ({ field, label, kind, width }): ColDef<SkuWaveRow> => ({
          colId: field,
          headerName: label,
          headerTooltip: label,
          width: width ?? 115,
          hide: !references.includes(field),
          valueGetter: params => params.data?.line?.[field] ?? null,
          valueFormatter: params =>
            params.value == null
              ? '—'
              : kind === 'text'
                ? String(params.value)
                : kind === 'money'
                  ? money(Number(params.value))
                  : kind === 'decimal'
                    ? Number(params.value).toLocaleString('en-US', {
                        minimumFractionDigits: 3,
                        maximumFractionDigits: 3,
                      })
                    : formatUnits(Number(params.value)),
          cellClass:
            kind === 'text' ? undefined : 'ag-right-aligned-cell wave-number',
          tooltipValueGetter: params =>
            params.value == null ? undefined : String(params.value),
        })
      ),
      ...weeks.map((week): ColGroupDef<SkuWaveRow> => {
        const metric = (
          key: keyof SkuWaveAllocation,
          name: string,
          width: number
        ): ColDef<SkuWaveRow> => ({
          colId: `${week.week}-${key}`,
          headerName: name,
          width,
          valueGetter: params => params.data?.waves[week.week]?.[key],
          valueFormatter: params =>
            params.value == null
              ? '—'
              : key === 'cubicFtPerDc'
                ? decimal(Number(params.value))
                : formatUnits(Number(params.value)),
          cellClass: 'ag-right-aligned-cell wave-number',
          headerClass: 'ag-right-aligned-header',
        })
        return {
          groupId: `wave-${week.week}`,
          headerName: `${week.event} · ${weekLabel(week.week)}`,
          marryChildren: true,
          headerClass: week.outside ? 'sku-outside-header' : 'sku-wave-group',
          headerGroupComponent: () => (
            <div className="flex w-full flex-col gap-1 text-xs leading-tight">
              <span className="font-semibold">
                {week.event}{' '}
                <span className="font-normal text-muted-foreground">
                  · {weekLabel(week.week)} · {formatWaveDate(week.date)}
                </span>
              </span>
              <span
                className={
                  week.outside ? 'text-amber-800' : 'text-muted-foreground'
                }
              >
                {week.outside
                  ? 'Outside overall plan'
                  : `Plan ${formatPercent(week.overallPercent)}`}
              </span>
            </div>
          ),
          children: [
            {
              ...metric('percent', 'Wave %', 102),
              valueGetter: params => {
                const value = params.data?.waves[week.week]?.percent
                return value == null ? null : value * 100
              },
              valueFormatter: params =>
                params.value == null
                  ? '—'
                  : formatPercent(Number(params.value) / 100),
              editable: params => !!params.data?.line && !params.node.rowPinned,
              cellClass: params =>
                `ag-right-aligned-cell wave-number ${params.node.rowPinned ? '' : 'wave-input-cell'} ${week.outside ? 'sku-outside-cell' : ''}`,
              headerComponentParams: {
                innerHeaderComponent: () => (
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <Pencil size={12} /> Wave %
                  </span>
                ),
              },
              cellEditor: SkuPercentEditor,
              cellEditorParams: { waveLabel: week.event },
              cellEditorPopup: true,
              suppressKeyboardEvent: params => {
                if (
                  params.editing &&
                  ['Enter', 'Tab'].includes(params.event.key) &&
                  params.event.target instanceof HTMLInputElement &&
                  validateWavePercent(params.event.target.value)
                ) {
                  params.event.preventDefault()
                  return true
                }
                return false
              },
              tooltipValueGetter: params =>
                `${params.data?.line?.sku ?? 'Filtered totals'} · ${week.event}: ${params.value == null ? '—' : formatPercent(Number(params.value) / 100)}${params.data?.line ? ' · Editable' : ''}`,
            },
            metric('qty', 'Wave Qty', 108),
            { ...metric('avgStore', 'Avg Store Units', 132), hide: !metrics },
            {
              ...metric('cubicFtPerDc', 'Avg Cubic Ft / DC', 144),
              hide: !metrics,
            },
          ],
        }
      }),
      {
        colId: 'actions',
        headerName: '',
        width: mobile ? 44 : 82,
        pinned: 'right',
        lockPinned: true,
        sortable: false,
        resizable: false,
        cellClass: 'sku-actions-cell',
        cellRenderer: ({ data }: ICellRendererParams<SkuWaveRow>) => {
          if (!data?.line) return null
          const line = data.line
          const count = commentCounts[line.id] ?? 0
          return (
            <div className="flex flex-col items-center justify-center sm:flex-row">
              <Button
                id={`sku-comments-${line.id}`}
                variant="ghost"
                size="icon"
                className="relative h-7 w-8"
                title={`${count} comments`}
                aria-label={`Comments for ${line.sku} ${line.type}`}
                onClick={event => onOpenComments(line, event.currentTarget)}
              >
                <MessageCircle size={15} />
                {count > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-0.5 text-[9px] text-primary-foreground">
                    {count}
                  </span>
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-8"
                disabled={!data.adjusted}
                title="Reset row to overall plan"
                aria-label={`Reset row ${line.sku} ${line.type}`}
                onClick={() => onResetRow(line.id)}
              >
                <RotateCcw size={13} />
              </Button>
            </div>
          )
        },
      },
    ]
  }, [
    mobile,
    references,
    separateProduct,
    weeks,
    metrics,
    commentCounts,
    onResetRow,
    onOpenComments,
  ])

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3">
        <div className="relative min-w-44 flex-1 sm:max-w-72">
          <Search
            size={15}
            className="absolute left-3 top-2.5 text-muted-foreground"
          />
          <Input
            aria-label="Search SKU lines"
            placeholder="Search SKU, product or vendor..."
            value={query}
            onChange={event => setQuery(event.target.value)}
            className="h-9 bg-secondary/40 pl-9 text-xs"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={reviewOnly ? 'secondary' : 'outline'}
            size="sm"
            aria-pressed={reviewOnly}
            onClick={() => setReviewOnly(!reviewOnly)}
          >
            <AlertTriangle size={14} /> Needs review{' '}
            <span className="text-muted-foreground">
              {rows.filter(row => row.needsReview).length}
            </span>
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline">
                <Columns3 size={14} /> Columns{' '}
                {(references.length > 0 || separateProduct) &&
                  `(${references.length + (separateProduct ? 1 : 0)})`}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-3">
              <div className="mb-2">
                <p className="text-sm font-semibold">Reference columns</p>
                <div className="mt-1 flex items-center justify-between">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setReferences(
                        referenceColumns.map(column => column.field)
                      )
                      setSeparateProduct(true)
                    }}
                  >
                    Show all
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setReferences([])
                      setSeparateProduct(false)
                    }}
                  >
                    Hide all
                  </Button>
                </div>
              </div>
              <label className="mb-2 flex cursor-pointer items-center gap-2 border-b px-2 pb-3 text-xs">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--color-primary)]"
                  checked={separateProduct}
                  onChange={event => setSeparateProduct(event.target.checked)}
                />
                Separate SKU and description
              </label>
              <div className="max-h-72 overflow-y-auto">
                {referenceColumns.map(column => (
                  <label
                    key={column.field}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-xs hover:bg-secondary"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[var(--color-primary)]"
                      checked={references.includes(column.field)}
                      onChange={event =>
                        setReferences(current =>
                          event.target.checked
                            ? [...current, column.field]
                            : current.filter(field => field !== column.field)
                        )
                      }
                    />
                    {column.label}
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <Button
            size="sm"
            variant={metrics ? 'secondary' : 'outline'}
            aria-pressed={metrics}
            onClick={() => setMetrics(!metrics)}
          >
            <SlidersHorizontal size={14} /> Wave metrics
          </Button>
        </div>
      </div>
      <div
        className="sku-wave-grid wave-planning-grid ag-theme-michaels w-full"
        aria-label="SKU wave allocations"
      >
        <AgGridReact<SkuWaveRow>
          ref={grid}
          theme="legacy"
          rowData={filtered}
          columnDefs={columns}
          getRowId={params => params.data.id}
          pinnedBottomRowData={totals}
          defaultColDef={{
            sortable: true,
            resizable: true,
            suppressMovable: true,
            cellDataType: false,
            wrapHeaderText: true,
          }}
          rowHeight={mobile ? 68 : 58}
          headerHeight={42}
          groupHeaderHeight={58}
          singleClickEdit
          readOnlyEdit
          stopEditingWhenCellsLoseFocus
          invalidEditValueMode="revert"
          ensureDomOrder
          tooltipShowDelay={450}
          overlayNoRowsTemplate="<span class='text-sm'>No matching lines. Adjust your search or filters.</span>"
          onCellEditRequest={event => {
            const week = weeks.find(
              item => `${item.week}-percent` === event.column.getColId()
            )
            if (week && event.data.line)
              onEdit(event.data.id, week.index, String(event.newValue ?? ''))
          }}
          onFirstDataRendered={event => {
            if (mobile && weeks[0])
              event.api.ensureColumnVisible(`${weeks[0].week}-percent`, 'start')
          }}
          tabToNextCell={params => {
            if (!params.editing) return params.nextCellPosition ?? false
            const current = weeks.findIndex(
              week =>
                `${week.week}-percent` ===
                params.previousCellPosition.column.getColId()
            )
            const direction = params.backwards ? -1 : 1
            let index = current + direction
            let rowIndex = params.previousCellPosition.rowIndex
            if (index < 0 || index >= weeks.length) {
              index = params.backwards ? weeks.length - 1 : 0
              rowIndex += direction
            }
            if (rowIndex < 0 || rowIndex >= params.api.getDisplayedRowCount())
              return false
            const column = params.api.getColumn(`${weeks[index].week}-percent`)
            return column ? { rowIndex, rowPinned: null, column } : false
          }}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-secondary/30 px-3 py-2 text-[11px] text-muted-foreground">
        <span>
          {filtered.length} of {rows.length} lines · {weeks.length} wave dates
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Pencil size={11} /> Wave % is editable · Other values are calculated
          or reference data
        </span>
        {rows.every(row => !row.needsReview) && (
          <span className="inline-flex items-center gap-1">
            <Check size={12} /> All lines allocated
          </span>
        )}
      </div>
    </>
  )
}
